import { InputCopy } from "@/components/molecules/input-copy";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useAdminTenantUsers,
  useRemoveTenantUser,
  useResetUserPassword,
  type TenantUser,
} from "@/features/admin";
import { useUser } from "@/features/auth/hooks/use-user";
import { DotsThreeOutlineIcon, Key, TrashIcon } from "@phosphor-icons/react";
import { useState } from "react";
import { toast } from "sonner";

type PendingAction = { type: "reset" | "remove"; user: TenantUser };

/**
 * Why a password reset can be refused. Resetting an administrator would hand over
 * cross-tenant access, and resetting your own account bypasses the self-service flow —
 * the server enforces both, this only mirrors them in the UI.
 */
const resetBlockedReason = (
  user: TenantUser,
  currentUserId?: string,
): string | null => {
  if (user.user_id === currentUserId) return "Votre propre compte";
  if (
    user.account_role === "SYSADMIN" ||
    user.account_role === "SYSADMIN_READ" ||
    user.account_role === "SYSTEM"
  ) {
    return "Compte administrateur";
  }
  return null;
};

/** Members of the selected tenant, with password reset + access removal for hand-over. */
export function TenantUsers({ tenantId }: { tenantId: string }) {
  const { data: users = [], isLoading } = useAdminTenantUsers(tenantId);
  const { data: currentUser } = useUser();
  const resetPassword = useResetUserPassword();
  const removeUser = useRemoveTenantUser();
  const [pending, setPending] = useState<PendingAction | null>(null);
  const [reset, setReset] = useState<{
    email: string;
    password: string;
  } | null>(null);

  const handleReset = async (user: TenantUser) => {
    try {
      const result = await resetPassword.mutateAsync({ userId: user.user_id });
      setReset(result);
      toast.success("Nouveau mot de passe généré");
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Échec de la réinitialisation",
      );
    }
  };

  const handleRemove = async (user: TenantUser) => {
    try {
      await removeUser.mutateAsync({ tenantId, userId: user.user_id });
      toast.success("Accès retiré");
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Échec du retrait de l'accès",
      );
    }
  };

  const handleConfirm = async () => {
    if (!pending) return;
    const { type, user } = pending;
    setPending(null);
    if (type === "reset") await handleReset(user);
    else await handleRemove(user);
  };

  if (!tenantId) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-sm text-muted-foreground">
          Sélectionnez un tenant pour voir ses utilisateurs.
        </CardContent>
      </Card>
    );
  }

  const isBusy = resetPassword.isPending || removeUser.isPending;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Utilisateurs du tenant</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {isLoading ? (
          <>
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </>
        ) : users.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Aucun utilisateur rattaché.
          </p>
        ) : (
          users.map((user) => {
            const blocked = resetBlockedReason(user, currentUser?.id);

            return (
              <div
                key={user.user_id}
                className="flex items-center justify-between gap-3 rounded-md border p-3"
              >
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium">
                    {user.email}
                  </div>
                  <div className="mt-1 flex items-center gap-2">
                    <Badge variant="outline">{user.role}</Badge>
                    {user.status !== "ACTIVE" ? (
                      <Badge variant="outline">{user.status}</Badge>
                    ) : null}
                    {blocked ? (
                      <span className="text-xs text-muted-foreground">
                        {blocked}
                      </span>
                    ) : null}
                    {!user.has_password ? (
                      <span className="text-xs text-amber-600 dark:text-amber-400">
                        pas de mot de passe
                      </span>
                    ) : null}
                  </div>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      className="h-8 w-8 p-0"
                      disabled={isBusy}
                    >
                      <span className="sr-only">Actions</span>
                      <DotsThreeOutlineIcon weight="fill" className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      disabled={!!blocked}
                      onClick={() => setPending({ type: "reset", user })}
                    >
                      <Key className="mr-2 h-4 w-4" />
                      Réinitialiser le mot de passe
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      className="text-destructive"
                      onClick={() => setPending({ type: "remove", user })}
                    >
                      <TrashIcon className="mr-2 h-4 w-4" weight="fill" />
                      Retirer
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            );
          })
        )}

        {reset ? (
          <div className="space-y-2 rounded-md border border-emerald-500/40 p-3">
            <div className="text-sm font-medium">
              Nouveau mot de passe pour {reset.email}
            </div>
            <InputCopy value={reset.password} />
            <p className="text-xs text-muted-foreground">
              Affiché une seule fois.
            </p>
          </div>
        ) : null}
      </CardContent>

      <Dialog
        open={!!pending}
        onOpenChange={(open) => !open && setPending(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {pending?.type === "remove"
                ? "Retirer l'accès ?"
                : "Réinitialiser le mot de passe ?"}
            </DialogTitle>
            <DialogDescription>
              {pending?.type === "remove" ? (
                <>
                  {pending?.user.email} n'aura plus accès à ce tenant. Le compte
                  utilisateur est conservé et l'accès peut être redonné.
                </>
              ) : (
                <>
                  Un nouveau mot de passe sera généré pour {pending?.user.email}
                  . L'ancien mot de passe cessera immédiatement de fonctionner.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPending(null)}>
              Annuler
            </Button>
            <Button
              variant={pending?.type === "remove" ? "destructive" : "default"}
              disabled={isBusy}
              onClick={handleConfirm}
            >
              {pending?.type === "remove" ? "Retirer" : "Réinitialiser"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
